import Seo from "@/components/Seo";
import PageHero from "@/components/PageHero";
import PtfButton from "@/components/PtfButton";
import { Reveal, MaskText } from "@/components/motion/Reveal";
import { EvidenceChip, VerifyFlag } from "@/components/Evidence";
import { DAWN_TEAM } from "@/lib/content";
import { Mountain, Waves, LifeBuoy, Compass, Users } from "lucide-react";

const Eyebrow = ({ children, light }) => (
  <p className={`text-[0.7rem] uppercase tracking-[0.28em] mb-5 flex items-center gap-3 ${light ? "text-ptf-accent" : "text-ptf-primary/60"}`}>
    <span className={`h-px w-8 inline-block ${light ? "bg-ptf-accent" : "bg-ptf-primary/40"}`} />
    {children}
  </p>
);

const NIKHIL_CREDENTIALS = [
  "Adventurer & mountaineer",
  "Kayaker & white-water rafting expert",
  "Surfer & climber",
  "Wilderness expert",
  "Scout trainer",
  "Resuscitation, First Aid & Lifeguard qualified",
];

const NIKHIL_SECTIONS = [
  ["The person", "Born in 1992 in Ballia, Uttar Pradesh, and an alumnus of Kendriya Vidyalaya, he graduated in 2015, held a job for four months, and left it for the field. A social entrepreneur made by the outdoors."],
  ["The adventurer", "Mountains, rivers and oceans first: mountaineering, kayaking, white-water rafting, surfing and climbing, practised for years before any of it had a name like Planet Trotter."],
  ["The field work", "A 2019 recce to open a rafting route found the Ladhiya Valley instead. Then came the 70-kilometre trek in, the adoption of three villages, and the interventions that followed: solar, water, medicine and education, documented in the Project Dawn reports."],
  ["Project Dawn", "Dawn is his framework: adventure as the connection between travellers and the communities who host them, with the SDGs as the measure of what the connection achieves."],
  ["The mission", "To prove that responsible exploration can fund and sustain rural development, and to train the next generation of village children in the outdoor sports that changed his own life."],
];

function FounderProfile() {
  return (
    <section data-testid="founder-profile" className="bg-ptf-bg py-16 md:py-24">
      <div className="mx-auto max-w-[1600px] px-5 sm:px-8 lg:px-12 grid grid-cols-1 lg:grid-cols-12 gap-12 items-start">
        <div className="lg:col-span-5">
          <Reveal>
            <figure className="lg:sticky lg:top-28">
              <img src="/dawn/kilimanjaro-summit.jpg" alt="Nikhil Pratap Singh with the Project Dawn flag at Uhuru Peak, Kilimanjaro" className="w-full object-cover" loading="lazy" />
              <figcaption className="mt-2 flex items-center justify-between gap-3 text-[0.65rem] uppercase tracking-[0.12em] text-ptf-text/55">
                Uhuru Peak, Kilimanjaro · 5,895 m
                <EvidenceChip status="DOCUMENTED" />
              </figcaption>
            </figure>
          </Reveal>
        </div>
        <div className="lg:col-span-7">
          <Reveal><Eyebrow>Founder</Eyebrow></Reveal>
          <MaskText
            lines={["Nikhil", "Pratap Singh."]}
            className="font-display font-extrabold uppercase text-5xl sm:text-6xl leading-[0.92] tracking-tight text-ptf-primary"
          />
          <Reveal delay={0.15}>
            <p className="mt-6 text-lg text-ptf-text/80 leading-relaxed max-w-2xl">
              The person behind Planet Trotter has practically lived out of a suitcase:
              researching, exploring, writing and filming. The credentials below are the
              ones documented in the project material. Nothing more is claimed.
            </p>
            <div className="mt-7 flex flex-wrap gap-2" data-testid="nikhil-credentials">
              {NIKHIL_CREDENTIALS.map((c) => (
                <span key={c} className="text-[0.68rem] uppercase tracking-[0.12em] border border-ptf-primary/25 text-ptf-primary px-3 py-1.5">{c}</span>
              ))}
            </div>
          </Reveal>
          <div className="mt-12 space-y-0">
            {NIKHIL_SECTIONS.map(([t, x], i) => (
              <Reveal key={t} delay={i * 0.05}>
                <div className="py-7 border-t border-ptf-primary/12 grid grid-cols-1 md:grid-cols-12 gap-3">
                  <div className="md:col-span-4 font-display uppercase text-2xl tracking-tight text-ptf-primary">{t}</div>
                  <div className="md:col-span-8 text-ptf-text/70 leading-relaxed">{x}</div>
                </div>
              </Reveal>
            ))}
          </div>
          <Reveal delay={0.2}>
            <p className="mt-8 text-sm italic font-serif-ed text-ptf-secondary">
              The organisation is larger than one person. The field team below, and the
              communities themselves, carry the work.
            </p>
          </Reveal>
        </div>
      </div>
    </section>
  );
}

function FieldTeam() {
  return (
    <section data-testid="field-team" className="bg-ptf-primary text-ptf-bg py-16 md:py-24 grain">
      <div className="mx-auto max-w-[1600px] px-5 sm:px-8 lg:px-12">
        <div className="max-w-3xl mb-12">
          <Reveal><Eyebrow light>Experts & field team</Eyebrow></Reveal>
          <MaskText
            lines={["The people in", "the field."]}
            className="font-display font-extrabold uppercase text-4xl sm:text-5xl leading-[0.92] tracking-tight"
          />
          <Reveal delay={0.15}>
            <p className="mt-5 text-ptf-bg/70 leading-relaxed">
              Full bios for the field team are being gathered and verified before
              publication. No qualifications are invented in the meantime.
            </p>
          </Reveal>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-px bg-ptf-bg/10 border border-ptf-bg/10 max-w-2xl">
          {DAWN_TEAM.map((p, i) => (
            <Reveal key={p.name} delay={(i % 5) * 0.05} className="h-full">
              <div data-testid={`team-${i}`} className="bg-ptf-primary p-7 h-full flex flex-col">
                {p.photo ? (
                  <img
                    data-testid={`team-photo-${i}`}
                    src={p.photo}
                    alt={p.name}
                    className="h-24 w-24 rounded-full object-cover object-top mb-5 border-2 border-ptf-accent/40"
                    loading="lazy"
                  />
                ) : (
                  <span className="grid place-items-center h-14 w-14 rounded-full bg-ptf-bg/10 mb-5">
                    <Users className="h-6 w-6 text-ptf-accent" />
                  </span>
                )}
                <h3 className="font-display uppercase text-xl tracking-tight leading-none">{p.name}</h3>
                <p className="mt-2 text-xs uppercase tracking-[0.14em] text-ptf-bg/60 flex-1">{p.role}</p>
                {!p.documented && (
                  <p className="mt-4 text-[0.6rem] uppercase tracking-[0.12em] text-ptf-bg/40 border-t border-ptf-bg/10 pt-3">Bio pending verification</p>
                )}
              </div>
            </Reveal>
          ))}
        </div>
      </div>
    </section>
  );
}

export default function People() {
  return (
    <>
      <Seo title="Our People" path="/people" description="The founder and field team behind Planet Trotter and Project Dawn: adventurers, trainers and field workers documented in the project material." />
      <PageHero
        eyebrow="Our People"
        titleLines={["Lived out of", "a suitcase."]}
        intro="Planet Trotter's programmes are led by people who have researched, explored, written and filmed in the field for most of their lives."
        image="/dawn/vor-trek.jpg"
      />
      <FounderProfile />
      <FieldTeam />
      <section className="bg-ptf-secondary text-ptf-bg py-20 md:py-28">
        <div className="mx-auto max-w-[1600px] px-5 sm:px-8 lg:px-12 flex flex-col lg:flex-row lg:items-center lg:justify-between gap-8">
          <h2 className="font-display font-extrabold uppercase text-4xl md:text-5xl tracking-tight leading-[0.95]">The field always has<br />room for one more.</h2>
          <div className="flex flex-col sm:flex-row gap-3 shrink-0">
            <PtfButton to="/volunteer" variant="accent" data-testid="people-volunteer-cta">Join as a Volunteer</PtfButton>
            <PtfButton to="/exploration" variant="outlineLight">Join an Experience</PtfButton>
          </div>
        </div>
      </section>
    </>
  );
}
